import React, { FunctionComponent, useEffect, useLayoutEffect } from 'react';
import { Heading, Message } from 'components/chroma';
import { TextSmall } from 'components/chroma/extended';
import { ApiState } from 'scenes/common/helpers/apiData/types';
import { colesContactCenter } from 'scenes/common/helpers/contacts';
import { useFocus } from 'scenes/common/hooks/useFocus';
import usePublishAnalyticsEvent, {
  AnalyticsEvent,
} from 'scenes/payments/hooks/useAnalytics';
import { isPostCodeError } from './helper/errorHandling';
import { ErrorProps, GlobalErrors } from './types';

const ErrorMessages = {
  POST_CODE: (
    <>
      We could not match the policy number and postcode you entered. Please
      check that your details are correct and try again.
    </>
  ),
  GENERAL_API: (
    <>
      Please call us on <strong>1300 265 374</strong> as we need to clarify your
      policy details in order to process your payment.
      <br />
      Our contact hours can be found{' '}
      <a href={colesContactCenter()} target="_blank" rel="noopener noreferrer">
        here
      </a>
      .
    </>
  ),
  GENERAL: (
    <>
      Please note, due to a system error a renewal reminder was incorrectly sent
      to some Home and Landlord customers on Sat 10/9/2022 and Sun 11/9/2022.
      Please disregard unless relevant for your due date. We apologise for any
      inconvenience this may have caused.
    </>
  ),
};

// Required for analytics
const PlainTextErrorMessage = {
  POST_CODE:
    'We could not match the policy number and postcode you entered. Please check that your details are correct and try again.',
  GENERAL_API: `Please call us on 1300 265 374 as we need to clarify your policy details in order to process your payment. Our contact hours can be found <a href=${colesContactCenter()} target="_blank" rel="noopener noreferrer">here</a>.`,
};

const ErrorMessage: FunctionComponent<ErrorProps> = ({ type, data }) => {
  const analyticsErrorEvent = usePublishAnalyticsEvent(AnalyticsEvent.ERROR);
  const { elementRef, setFocus, cleanup } = useFocus<HTMLDivElement>();
  useEffect(() => {
    if (type === ApiState.BusinessError) {
      analyticsErrorEvent(
        isPostCodeError({ type, data })
          ? PlainTextErrorMessage.POST_CODE
          : PlainTextErrorMessage.GENERAL_API
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, type]);

  useLayoutEffect(() => {
    setFocus();
    return () => {
      cleanup();
    };
  }, [setFocus, cleanup]);

  return (
    <>
      {type === ApiState.BusinessError && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        <div tabIndex={0} ref={elementRef}>
          <Message error>
            <Heading size={6}>Something&apos;s gone wrong</Heading>
            <TextSmall>
              {isPostCodeError({ type, data }) && ErrorMessages.POST_CODE}
              {!isPostCodeError({ type, data }) && ErrorMessages.GENERAL_API}
            </TextSmall>
          </Message>
        </div>
      )}
      {type === GlobalErrors.loginMessage && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        <Message error>
          <TextSmall>{ErrorMessages.GENERAL}</TextSmall>
        </Message>
      )}
    </>
  );
};

export default ErrorMessage;
